import { unwrapResult } from "@reduxjs/toolkit";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import arrowForward from "../../../../assets/images/arrow_forward_button.svg";
import useHomeFlow from "../../../../hooks/useHomeFlowSimple";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import { selectHomeFormData } from "../../../../redux/features/homeFormSlice";
import { policyPay } from "../../../../redux/features/payment/policyPaySlice";
import { selectHomeQuote } from "../../../../redux/features/quoteSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import {
  applyPromoCodeToQuote,
  calculatePromoDiscount,
  getFinalQuoteNumberForPayment,
  getFinalPremiumForPayment,
} from "../../../../utils/promoCodeUtils";
import Button from "../../../common/Button/Button";
import SummaryContent from "../../../common/SummaryContent/SummaryContent";
import NewHomeJourneyLayout from "../../layouts/NewHomeJourneyLayout";

const Summary = () => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const isRTL = i18n.resolvedLanguage === "ar";
  const formData = useSelector(selectHomeFormData);
  const quoteData = useSelector(selectHomeQuote);
  const { handleNextSubstep, handlePrevSubstep } = useHomeFlow();
  const translate = useLanguageTranslate();

  const [promoCode, setPromoCode] = useState("");
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);
  const [promoCodeDiscount, setPromoCodeDiscount] = useState(0);
  const [promoAppliedQuoteData, setPromoAppliedQuoteData] = useState(null);
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Get data from Redux store
  const homeDetailsData = formData?.homeDetails || {};
  const addressDetailsData = formData?.addressDetails || {};
  const coversData = formData?.covers || {};

  // Get pricing from API response or calculate fallback
  const getPricingData = () => {
    if (quoteData) {
      // Use API response data directly (quoteData is already result.data from Redux)
      // Note: API returns lowercase property names after Redux transformation
      return {
        totalPremium: quoteData.totalPremium || quoteData.TotalPremium || 0,
        premium: quoteData.premium || quoteData.Premium || 0,
        tax: quoteData.tax || quoteData.Tax || 0,
        quoteNumber:
          quoteData.policyNumber ||
          quoteData.PolicyNumber ||
          quoteData.QuoteDetails?.QuoteNumber ||
          "N/A",
        sumInsured: quoteData.sumInsured || quoteData.SumInsured || 0,
      };
    }

    // Fallback to calculated values if no API data
    return {
      totalPremium: 0,
      premium: 0,
      tax: 0,
      quoteNumber: "N/A",
      sumInsured: 0,
    };
  };

  const pricingData = getPricingData();

  // Create plan data for summary
  // CRITICAL: When promo is applied, use values from promo API response
  const planData = {
    premium: promoCodeApplied && promoAppliedQuoteData
      ? promoAppliedQuoteData.Premium
      : pricingData.premium,
    basePremium: promoCodeApplied && promoAppliedQuoteData
      ? promoAppliedQuoteData.Premium
      : pricingData.premium,
    tax: promoCodeApplied && promoAppliedQuoteData
      ? promoAppliedQuoteData.Tax
      : pricingData.tax,
    totalPremium: promoCodeApplied && promoAppliedQuoteData
      ? promoAppliedQuoteData.TotalPremium
      : pricingData.totalPremium,
    quoteNumber: pricingData.quoteNumber,
    coverages: coversData,
    // Add API data and cover prices for home insurance covers display
    apiData: quoteData, // Pass the full quote data so SummaryContent can access covers
    coverPrices: coversData?.coverPrices || [], // Pass cover prices from form data
  };

  // Build home details table data to match design and new ranking
  const buildHomeData = () => {
    const data = {};

    // Calculate Total Sum Insured (Building Value + Content Value + Personal Belongings)
    const calculateTotalSumInsured = () => {
      const parseCleanFloat = (value) => {
        if (!value || value === "") return 0;
        return parseFloat(String(value).replace(/[,\s]/g, "")) || 0;
      };

      const buildingValue = parseCleanFloat(homeDetailsData?.buildingValue);
      const contentsValue = homeDetailsData?.contentsDetails
        ? parseCleanFloat(homeDetailsData?.contentsValue)
        : 0;
      const personalBelongingsValue = homeDetailsData?.contentsDetails
        ? parseCleanFloat(homeDetailsData?.personalBelongingsValue)
        : 0;

      return buildingValue + contentsValue + personalBelongingsValue;
    };

    // 1. House Number (Building Number)
    if (addressDetailsData?.buildingNumber) {
      data.houseNumber = addressDetailsData.buildingNumber;
    }

    // 2. Road Number
    if (addressDetailsData?.roadNumber) {
      data.roadNumber = addressDetailsData.roadNumber;
    }

    // 3. Block Number
    if (addressDetailsData?.blockNumber) {
      data.blockNumber = addressDetailsData.blockNumber;
    }

    // 4. City (Area Name)
    if (addressDetailsData?.areaName) {
      data.city = addressDetailsData.areaName;
    }

    // 5. Building Age
    if (addressDetailsData?.buildingAge) {
      const age = addressDetailsData.buildingAge;
      const arabicText = age > 1 ? `${age} سنوات` : `${age} سنة`;
      data.buildingAge = translate(
        `${age} years`,
        arabicText
      );
    }

    // 6. Total Sum Insured - Use API response if available, otherwise calculate
    let totalSumInsured = 0;

    if (pricingData.sumInsured > 0) {
      // Use SumInsured from API response (already extracted in getPricingData)
      totalSumInsured = pricingData.sumInsured;
    } else if (quoteData?.QuoteDetails?.SumInsured !== undefined) {
      // Try QuoteDetails.SumInsured as fallback
      totalSumInsured = parseFloat(quoteData.QuoteDetails.SumInsured);
    } else {
      // Fallback to calculated value
      totalSumInsured = calculateTotalSumInsured();
    }

    if (totalSumInsured > 0) {
      // Temporary fix: Divide by 2 because API response is wrong
      const adjustedAmount = totalSumInsured / 2;

      // Format with currency
      const formattedAmount = adjustedAmount.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      data.totalSumInsured = translate(
        `BHD ${formattedAmount}`,
        `${formattedAmount} د.ب`
      );
    }

    // 7. Add-ons - Check for selected covers using both old and new keys
    const addons = [];

    // All Risk (CPRO21)
    if (coversData?.allRisk || coversData?.cpro21) {
      addons.push(translate("All Risk", "جميع المخاطر"));
    }

    // VIP Cover (CPRO13)
    if (coversData?.vipCover || coversData?.cpro13) {
      addons.push(translate("VIP Cover", "تغطية كبار الشخصيات"));
    }

    // Domestic Helper (CPRO35)
    if (coversData?.domesticHelper || coversData?.cpro35) {
      addons.push(translate("Domestic Helper", "العامل المنزلي"));
    }

    // Terrorism (CPRO33)
    if (coversData?.sabotageAndTerrorism || coversData?.cpro33) {
      addons.push(translate("Terrorism Attack", "تغطية التخريب والإرهاب"));
    }

    if (addons.length > 0) {
      data.addons = addons;
    }

    return data;
  };

  const homeData = buildHomeData();

  const applyPromoCode = async () => {
    setIsApplyingPromo(true);

    try {
      const result = await applyPromoCodeToQuote(
        pricingData.quoteNumber,
        promoCode,
        translate
      );

      if (result.success) {
        const discount = calculatePromoDiscount(
          pricingData.totalPremium,
          result.data.TotalPremium
        );
        setPromoCodeApplied(true);
        setPromoCodeDiscount(discount);
        setPromoAppliedQuoteData(result.data);
        toast.success(
          translate("Promo code applied!", "تم تطبيق كود الخصم!")
        );
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error("Promo code application exception:", error);
      toast.error(
        translate(
          "Failed to apply promo code. Please try again.",
          "فشل تطبيق كود الخصم. يرجى المحاولة مرة أخرى."
        )
      );
    } finally {
      setIsApplyingPromo(false);
    }
  };

  const clearPromoCode = () => {
    setPromoCode("");
    setPromoCodeApplied(false);
    setPromoCodeDiscount(0);
    setPromoAppliedQuoteData(null);
  };

  const handleProceedToPayment = async () => {
    if (!agreedToTerms) {
      alert(
        translate(
          "Please agree to the terms and conditions",
          "يرجى الموافقة على الشروط والأحكام"
        )
      );
      return;
    }

    // Validate we have the necessary payment data
    if (!pricingData.quoteNumber || !pricingData.totalPremium) {
      toast.error(
        translate(
          "Unable to process payment. Quote information is missing.",
          "غير قادر على معالجة الدفع. معلومات التسعيرة مفقودة."
        )
      );
      console.error("Missing payment data:", {
        quoteNumber: pricingData.quoteNumber,
        totalPremium: pricingData.totalPremium,
      });
      return;
    }

    // CRITICAL: Set redirect flag IMMEDIATELY to prevent screen flash during payment processing
    sessionStorage.setItem("isRedirectingToPayment", "true");
    console.log("🔒 Set payment redirect flag - preventing flow reset during payment");

    // Get final quote number and premium (use promo values if applied)
    const finalQuoteNumber = getFinalQuoteNumberForPayment(
      promoCodeApplied,
      promoAppliedQuoteData,
      pricingData.quoteNumber
    );
    const finalPremium = getFinalPremiumForPayment(
      promoCodeApplied,
      promoAppliedQuoteData,
      pricingData.totalPremium
    );

    console.log("💰 Payment Data:", {
      promoCodeApplied,
      originalQuoteNumber: pricingData.quoteNumber,
      finalQuoteNumber,
      originalPremium: pricingData.totalPremium,
      finalPremium,
    });

    const payload = {
      PolicyNumber: finalQuoteNumber,
      Premium: finalPremium,
      Channel: getClientSource(),
    };

    setIsLoading(true);

    try {
      console.log("Initiating payment with payload:", payload);

      // Call the payment API
      const linkAction = await dispatch(policyPay(payload));
      const link = unwrapResult(linkAction);

      console.log("Payment link received:", link);

      if (link) {
        // Flag already set at the beginning - now just redirect
        console.log("🔄 Redirecting to payment gateway...");

        // Small delay to ensure state is saved, then redirect
        setTimeout(() => {
          window.location.href = link;
        }, 300);
      } else {
        // Clear flag on error
        sessionStorage.removeItem("isRedirectingToPayment");

        toast.error(
          translate(
            "Unable to generate payment link. Please try again.",
            "غير قادر على إنشاء رابط الدفع. يرجى المحاولة مرة أخرى."
          )
        );
      }
    } catch (error) {
      console.error("Payment initiation error:", error);

      // Clear flag on error
      sessionStorage.removeItem("isRedirectingToPayment");

      toast.error(
        translate(
          "Payment failed. Please try again.",
          "فشل الدفع. يرجى المحاولة مرة أخرى."
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <NewHomeJourneyLayout
        is_hide_back_btn={false}
        customBackHandler={handlePrevSubstep}
      >
        <div className="flex flex-col items-center p-6 max767:p-0 mx-auto pt-0 py-5 w-full pb-0 max767:pb-[0px]">
          <div className="max767:ml-[0px] max767:mt-0 max767:w-full max767:pb-0 max767:p-[22px] max767:pt-0 w-full max-w-2xl">
            {/* Header */}
            <div className="text-center max767:text-left mb-8">
              <h1 className="text-2xl font-semibold text-[#1A2024] mb-2 max767:rtl:text-right">
                {translate("Review and Confirm", "المراجعة والتأكيد")}
              </h1>
              <p className="text-[#1A2024] max767:text-left max767:rtl:text-right">
                {translate(
                  "Please review your information and proceed to payment.",
                  "يرجى مراجعة معلوماتك والانتقال إلى الدفع."
                )}
              </p>
            </div>

            {/* Policy Section */}
            <div className="mb-8">
              {/* Home Details Table */}
              <div className="bg-[#F6F8F9] rounded-xl p-4 mb-6 max767:p-0 max767:bg-transparent">
                <h2 className="text-base font-semibold text-[#1A2024] mb-2">
                  {translate(
                    "New Home Insurance Policy",
                    "وثيقة تأمين المنزل الجديدة"
                  )}
                </h2>
                <p className="text-sm rtl:font-medium rtl:text-[#1A2024] mb-4">
                  {translate(
                    "Review the details below and confirm the payment",
                    "قم بمراجعة التفاصيل أدناه وتأكيد الدفع"
                  )}
                </p>

                {/* Home Details Table - following new ranking order */}
                <div className="bg-white border border-[#E5E7EB] rounded-[10px] overflow-hidden">
                  {/* 1. House Number */}
                  {homeData.houseNumber && (
                    <div className={`flex ${homeData.roadNumber || homeData.blockNumber || homeData.city || homeData.buildingAge || homeData.totalSumInsured || (homeData.addons && homeData.addons.length > 0) ? "border-b border-[#E5E7EB] leading-[44px]" : ""}`}>
                      <div className="flex-1 px-4 bg-white border-r border-[#E5E7EB] rtl:border-l h-[44px] leading-[44px]">
                        <span className="text-[#5B6871] text-sm font-medium">
                          {translate("House Number", "رقم المبنى")}
                        </span>
                      </div>
                      <div className="flex-1 bg-white">
                        <div className="px-4 ">
                          <span className="text-[#1A2024] text-sm font-medium">
                            {homeData.houseNumber}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 2. Road Number */}
                  {homeData.roadNumber && (
                    <div className={`flex ${homeData.blockNumber || homeData.city || homeData.buildingAge || homeData.totalSumInsured || (homeData.addons && homeData.addons.length > 0) ? "border-b border-[#E5E7EB] leading-[44px]" : ""}`}>
                      <div className="flex-1 px-4 bg-white border-r border-[#E5E7EB] rtl:border-l h-[44px] leading-[44px]">
                        <span className="text-[#5B6871] text-sm font-medium">
                          {translate("Road Number", "رقم الشارع")}
                        </span>
                      </div>
                      <div className="flex-1 bg-white">
                        <div className="px-4 ">
                          <span className="text-[#1A2024] text-sm font-medium">
                            {homeData.roadNumber}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 3. Block Number */}
                  {homeData.blockNumber && (
                    <div className={`flex ${homeData.city || homeData.buildingAge || homeData.totalSumInsured || (homeData.addons && homeData.addons.length > 0) ? "border-b border-[#E5E7EB] leading-[44px]" : ""}`}>
                      <div className="flex-1 px-4 bg-white border-r border-[#E5E7EB] rtl:border-l h-[44px] leading-[44px]">
                        <span className="text-[#5B6871] text-sm font-medium">
                          {translate("Block Number", "رقم المربع")}
                        </span>
                      </div>
                      <div className="flex-1 bg-white">
                        <div className="px-4">
                          <span className="text-[#1A2024] text-sm font-medium">
                            {homeData.blockNumber}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 4. City */}
                  {homeData.city && (
                    <div className={`flex ${homeData.buildingAge || homeData.totalSumInsured || (homeData.addons && homeData.addons.length > 0) ? "border-b border-[#E5E7EB] leading-[44px]" : ""}`}>
                      <div className="flex-1 px-4 bg-white border-r border-[#E5E7EB] rtl:border-l h-[44px] leading-[44px]">
                        <span className="text-[#5B6871] text-sm font-medium">
                          {translate("City", "المدينة")}
                        </span>
                      </div>
                      <div className="flex-1 bg-white">
                        <div className="px-4">
                          <span className="text-[#1A2024] text-sm font-medium">
                            {homeData.city}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 5. Building Age */}
                  {homeData.buildingAge && (
                    <div className={`flex ${homeData.totalSumInsured || (homeData.addons && homeData.addons.length > 0) ? "border-b border-[#E5E7EB] leading-[44px]" : ""}`}>
                      <div className="flex-1 px-4 bg-white border-r border-[#E5E7EB] rtl:border-l h-[44px] leading-[44px]">
                        <span className="text-[#5B6871] text-sm font-medium">
                          {translate("Building Age", "عمر المبنى")}
                        </span>
                      </div>
                      <div className="flex-1 bg-white">
                        <div className="px-4">
                          <span className="text-[#1A2024] text-sm font-medium">
                            {homeData.buildingAge}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 6. Total Sum Insured */}
                  {homeData.totalSumInsured && (
                    <div className={`flex ${homeData.addons && homeData.addons.length > 0 ? "border-b border-[#E5E7EB] leading-[44px]" : ""}`}>
                      <div className="flex-1 px-4 bg-white border-r border-[#E5E7EB] rtl:border-l h-[44px] leading-[44px]">
                        <span className="text-[#5B6871] text-sm font-medium">
                          {translate("Total Sum Insured", "المبلغ المؤمن عليه")}
                        </span>
                      </div>
                      <div className="flex-1 bg-white h-[44px] leading-[44px]">
                        <div className="px-4">
                          <span className="text-[#1A2024] text-sm font-medium">
                            {homeData.totalSumInsured}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 7. Add-ons */}
                  {homeData.addons && homeData.addons.length > 0 && (
                    <div className="flex ">
                      <div className="flex-1 px-4 leading-[44px] bg-white border-r border-[#E5E7EB] rtl:border-l">
                        <span className="text-[#5B6871] text-sm font-medium">
                          {translate("Add-ons", "الإضافات")}
                        </span>
                      </div>
                      <div className="flex-1 bg-white">
                        <div className="">
                          {homeData.addons.map((addon, idx) => (
                            <div key={idx} className={`text-[#1A2024] px-4 h-[44px] leading-[44px] text-sm font-medium ${idx > 0 ? "border-t border-[#E5E7EB] h-[44px] leading-[44px]" : ""}`}>
                              {addon}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Summary Section */}
            <div className="mb-8">
              <div className="bg-[#F6F8F9] rounded-xl p-4">
                <SummaryContent
                  planData={planData}
                  promoCode={promoCode}
                  setPromoCode={setPromoCode}
                  applyPromoCode={applyPromoCode}
                  clearPromoCode={clearPromoCode}
                  promoCodeApplied={promoCodeApplied}
                  promoCodeDiscount={promoCodeDiscount}
                  isApplyingPromo={isApplyingPromo}
                  showQuotationNumber={true}
                  showPromoCode={true}
                  className="!bg-transparent"
                  flowType="home"
                />
              </div>
            </div>

            {/* Policy Notice */}
            <div className="mb-6 p-4 bg-[#FDE9D2] border border-[#FBC98E] rounded-lg flex items-start space-x-3 rtl:space-x-reverse">
              <div className="flex-shrink-0 mt-0.5">
                <svg
                  className="w-5 h-5 text-[#A56213]"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <p className="text-[#252C32] font-semibold text-sm">
                  {translate(
                    "Kindly note that the policy will be issued under your name",
                    "يرجى ملاحظة أن الوثيقة ستصدر باسمك"
                  )}
                </p>
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="mb-6">
              <label className="flex items-start space-x-3 rtl:space-x-reverse cursor-pointer">
                <label class="flex items-center cursor-pointer gap-2">
                  <input
                    type="checkbox"
                    class="peer hidden"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                  />
                  <div
                    class="size-5 border-2 border-gray-400 rounded-[4px] 
              peer-checked:bg-[#00A9E7] peer-checked:border-[#00A9E7] 
              flex items-center justify-center transition-all"
                  >
                    <svg
                      width="12"
                      height="9"
                      viewBox="0 0 12 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M11 0C10.72 0 10.47 0.11 10.29 0.29L4 6.59L1.71 4.29C1.53 4.11 1.28 4 1 4C0.45 4 0 4.45 0 5C0 5.28 0.11 5.53 0.29 5.71L3.29 8.71C3.47 8.89 3.72 9 4 9C4.28 9 4.53 8.89 4.71 8.71L11.71 1.71C11.89 1.53 12 1.28 12 1C12 0.45 11.55 0 11 0Z"
                        fill="white"
                      />
                    </svg>
                  </div>
                </label>
                {/* <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 size-5 text-[#00A9E7] bg-gray-100 border-gray-300 rounded focus:ring-[#00A9E7] focus:ring-2"
                /> */}
                <div className="text-sm text-[#252C32] rtl:font-medium leading-[24px] text-justify">
                  <span>
                    {translate(
                      "I hereby certify that the above given information is true and correct to the best of my knowledge, and I agree to cooperate fully by providing all requested documents before payment. I understand that providing invalid information will result in my insurance policy to be void. I also read and agree the",
                      "أقر بموجب هذا بأن المعلومات المذكورة أعلاه صحيحة ودقيقة على حد علمي، وأوافق على التعاون الكامل من خلال تقديم جميع الوثائق المطلوبة قبل الدفع. أفهم أن تقديم معلومات غير صحيحة سيؤدي إلى إلغاء وثيقة التأمين الخاصة بي. كما قرأت وأوافق على"
                    )}
                  </span>{" "}
                  <button
                    className="text-[#00384D] underline font-bold hover:text-[#0088CC]"
                    onClick={() =>
                      window.open(
                        "https://content.solidarity.bh/uploads/Home_Insurance_Wording_4de074be0d_1ac2dab698.pdf",
                        "_blank"
                      )
                    }
                  >
                    {translate("Policy Specification", "مواصفات وثيقة")}
                  </button>{" "}
                  <span>
                    {translate(
                      "of this Insurance product.",
                      "التأمين هذه."
                    )}
                  </span>
                </div>
              </label>
            </div>

            {/* Proceed to Payment Button - Desktop */}
            <div className="max767:hidden">
              <Button
                onSubmit={handleProceedToPayment}
                disabled={!agreedToTerms || isLoading}
                isLoading={isLoading}
                label={translate("Proceed to Payment", "انتقل إلى الدفع")}
                post_icon={arrowForward}
                post_icon_class={isRTL ? "rotate-180" : ""}
                className="w-full mt-6 py-4 px-6 rounded-[10px] gap-2"
              />
            </div>
          </div>
        </div>

        {/* Proceed to Payment Button - Mobile (Fixed at Bottom) */}
        <div className="hidden max767:block fixed bottom-0 left-0 w-full bg-white border-t border-[#E5E9EB] p-4 pb-6 z-[9] max767:pb-[max(1rem,env(safe-area-inset-bottom))]">
          <Button
            onSubmit={handleProceedToPayment}
            disabled={!agreedToTerms || isLoading}
            isLoading={isLoading}
            label={translate("Proceed to Payment", "انتقل إلى الدفع")}
            post_icon={arrowForward}
            post_icon_class={isRTL ? "rotate-180" : ""}
            className="w-full py-4 px-6 rounded-[10px] max767:h-12"
            fontSize="text-sm"
          />
        </div>
      </NewHomeJourneyLayout>
    </div>
  );
};

export default Summary;
